import { Trash } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useContactActions } from 'contacts/hooks';
import { IContact } from 'contacts/types';
import { Button, Confirmation } from 'core/components';
import { useAgGridTableContext } from 'core/states';
import { Color, Variant } from 'theme/enums';

interface IProps {
  onSubmitSuccess?: () => void;
}

export const RemoveContactButton = ({ onSubmitSuccess }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { t } = useTranslation();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { removeContacts } = useContactActions();

  const getTitle = () => {
    if (selectedRows.length === 1) {
      const contact = selectedRows[0] as IContact;

      return `Remove ${contact.name || contact.email || 'Contact'}`;
    }

    return t('titleRemoveContact', { count: selectedRows.length });
  };

  const getMessage = () => {
    if (selectedRows.length === 1) {
      const contact = selectedRows[0] as IContact;
      const contactName = contact.name || contact.email || 'contact';

      return `Are you sure you want to remove ${contactName}?`;
    }

    return t('messageConfirmRemoveContact', { count: selectedRows.length });
  };

  const handleRemoveContact = async () => {
    const contactIds = selectedRows.map((contact: IContact) => contact.id);

    const { success } = await removeContacts(contactIds);

    if (success) {
      setIsConfirmationOpen(false);
      deselectAllRows();
      onSubmitSuccess?.();
    }
  };

  const isDisabled = useMemo(
    () => selectedRows.length === 0 || selectedRows.some((row: IContact) => row.deleted),
    [selectedRows]
  );

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={isDisabled ? Variant.OUTLINED : Variant.SOFT}
        onClick={() => setIsConfirmationOpen(true)}
        startDecorator={<Trash />}
        disabled={isDisabled}
      />

      <Confirmation
        isOpen={isConfirmationOpen}
        title={getTitle()}
        message={getMessage()}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={handleRemoveContact}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
